// Turns one agent reasoning event's raw text into a { title, body } bubble.
//
// Most events are prose, but some `llm.response_received` steps carry the
// agent's *structured* planning output instead — a fenced or bare JSON blob
// like {"requests":[{target,family,priority,reason}, …]}. Dumping that JSON
// into the chat reads as a bug (see the "```json {…}" bubbles). We parse it and
// surface the human-meaningful bits (the per-request `reason`s) instead.

import type { ScanEvent, NarrateItem } from "../api";

export type Narration = { title: string; body: string };

/** One rendered chat bubble: an emoji glyph + a headline + optional body. */
export type FeedBubble = { emoji: string; title: string; body: string };

/** Optional localized labels; callers pass i18n strings, tests pass literals. */
export type NarrationLabels = {
  /** "Planning {n} attack vectors" — {n} replaced with the request count. */
  planning?: string;
  /** Neutral fallback when the step is JSON we don't specifically handle. */
  structured?: string;
};

/** Labels for the deterministic (no-LLM) fallback layer. */
export type FallbackLabels = NarrationLabels & {
  findingCritical: string;
  findingHigh: string;
  findingMedium: string;
  findingLow: string;
  findingInfo: string;
  exploit: string;
  step: string;
  milestone: string;
};

/** Which event kinds feed the chat. The `/events` endpoint takes this verbatim
 *  as its `kinds=` filter, so it doubles as the fetch allow-list. */
export const FEED_KINDS = [
  "finding.added",
  "llm.response_received",
  "agent.tool_executed",
  "scan.completed",
  "browser.authenticated_crawl",
  "mission.audit_summary",
].join(",");

export type BubbleKind = "finding" | "reasoning" | "tool" | "milestone";

const KIND_MAP: Record<string, BubbleKind> = {
  "finding.added": "finding",
  "llm.response_received": "reasoning",
  "agent.tool_executed": "tool",
  "tool.executed": "tool",
};

/** Bucket a raw event_type into one of our four bubble kinds. */
export function classifyEvent(eventType: string): BubbleKind {
  return KIND_MAP[eventType] ?? "milestone";
}

type LlmPayload = { response_text?: string; tool_calls_count?: number };
type FindingPayload = { severity?: string; vuln_type?: string; vulnerability?: string; target?: string; host?: string; status?: string };

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

/** Emoji + label for a finding's severity. */
function severityStyle(severity: string | undefined, labels: FallbackLabels): { emoji: string; label: string } {
  switch ((severity ?? "").toLowerCase()) {
    case "critical":
      return { emoji: "🔴", label: labels.findingCritical };
    case "high":
      return { emoji: "🔴", label: labels.findingHigh };
    case "medium":
      return { emoji: "🟠", label: labels.findingMedium };
    case "low":
      return { emoji: "🟡", label: labels.findingLow };
    default:
      return { emoji: "ℹ️", label: labels.findingInfo };
  }
}

/**
 * Map a raw scan event to the compact payload we send to the narration worker.
 * Returns null for events with nothing worth narrating (so the feed stays a
 * highlight reel, not a 4000-line log).
 */
export function eventToNarrateItem(ev: ScanEvent): NarrateItem | null {
  const kind = classifyEvent(ev.event_type);
  const payload = (ev.payload ?? {}) as LlmPayload & FindingPayload;

  if (kind === "finding") {
    return {
      id: ev.event_id,
      kind,
      text: str(ev.message),
      severity: str(payload.severity),
      vuln_type: str(payload.vuln_type) ?? str(payload.vulnerability),
      target: str(payload.target) ?? str(payload.host),
    };
  }

  if (kind === "reasoning") {
    const text = str(payload.response_text) ?? str(ev.message);
    if (!text) return null;
    return { id: ev.event_id, kind, text };
  }

  // tool | milestone — driven by the event's one-line message.
  const text = str(ev.message);
  if (!text) return null;
  return { id: ev.event_id, kind, text };
}

/**
 * Deterministic, no-LLM bubble for one event. This is the Layer-1 fallback used
 * when the narration worker is disabled or unreachable — always available,
 * instant, free. Returns null for events with nothing to show.
 */
export function fallbackBubble(ev: ScanEvent, labels: FallbackLabels): FeedBubble | null {
  const kind = classifyEvent(ev.event_type);
  const payload = (ev.payload ?? {}) as LlmPayload & FindingPayload;

  if (kind === "finding") {
    const { emoji, label } = severityStyle(payload.severity, labels);
    const vuln = str(payload.vuln_type) ?? str(payload.vulnerability) ?? "";
    const target = str(payload.target) ?? str(payload.host) ?? "";
    return { emoji, title: vuln ? `${label}: ${vuln}` : label, body: target };
  }

  if (kind === "reasoning") {
    const text = str(payload.response_text) ?? str(ev.message);
    if (!text) return null;
    const narr = narrateReasoning(text, labels);
    if (!narr.title && !narr.body) return null;
    const emoji = (payload.tool_calls_count ?? 0) > 0 ? "🕷️" : "🧠";
    return { emoji, title: narr.title || narr.body, body: narr.title ? narr.body : "" };
  }

  if (kind === "tool") {
    const text = str(ev.message);
    if (!text) return null;
    return { emoji: "🕷️", title: text, body: "" };
  }

  // milestone
  const text = str(ev.message);
  if (!text) return null;
  return { emoji: "✅", title: text, body: "" };
}

type PlanRequest = {
  target?: unknown;
  family?: unknown;
  priority?: unknown;
  reason?: unknown;
};

const strip = (l: string) => l.replace(/^[#>*\-•\d.)\s]+/, "").trim();

/** Peel a leading/trailing markdown code fence (``` or ```json) if present. */
function unfence(text: string): string {
  const t = text.trim();
  if (!t.startsWith("```")) return t;
  return t
    .replace(/^```[a-zA-Z0-9]*\s*/, "")
    .replace(/\s*```$/, "")
    .trim();
}

/** Parse JSON only when the (de-fenced) text actually looks like an object/array. */
function tryParseJson(text: string): unknown {
  const t = unfence(text);
  if (!(t.startsWith("{") || t.startsWith("["))) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return undefined;
  }
}

function asString(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** A short "family on target — reason" line for one planned request. */
function describeRequest(req: PlanRequest): string {
  const family = asString(req.family);
  const reason = asString(req.reason);
  const head = family || asString(req.target);
  if (head && reason) return `${head} — ${reason}`;
  return reason || head;
}

function fill(template: string | undefined, fallback: string, n: number): string {
  return (template ?? fallback).replace("{n}", String(n));
}

/**
 * Split prose into a headline + body, or summarize a structured JSON plan.
 * Pure and side-effect-free so the chat bubble stays trivially testable.
 */
export function narrateReasoning(
  reasoning: string | undefined,
  labels: NarrationLabels = {},
): Narration {
  if (!reasoning) return { title: "", body: "" };

  const parsed = tryParseJson(reasoning);
  if (parsed && typeof parsed === "object") {
    const requests = (parsed as { requests?: unknown }).requests;
    if (Array.isArray(requests) && requests.length > 0) {
      const title = fill(labels.planning, "Planning {n} attack vectors", requests.length);
      const body = requests
        .map((r) => describeRequest(r as PlanRequest))
        .filter(Boolean)
        .join(" · ");
      return { title, body };
    }
    // Some other JSON payload — never dump braces into the chat.
    return { title: labels.structured ?? "Working through a step…", body: "" };
  }

  // Prose: first meaningful line is the headline, the rest is the body.
  const text = unfence(reasoning);
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { title: "", body: "" };
  let title = strip(lines[0]);
  let body = lines.slice(1);
  // A bare list/heading marker leaves an empty title — pull the next line up.
  if (title.length < 3 && body.length) {
    title = strip(body[0]);
    body = body.slice(1);
  }
  // Cap an over-long headline at its first sentence so it stays one tidy line.
  if (title.length > 96) {
    const m = title.match(/^(.+?[.!?])\s/);
    if (m) {
      body.unshift(title.slice(m[1].length).trim());
      title = m[1].trim();
    } else {
      body.unshift(title.slice(92).trim());
      title = title.slice(0, 92).trimEnd() + "…";
    }
  }
  return { title, body: body.join(" ").replace(/\s+/g, " ").trim() };
}
